import React, { useState } from 'react';

import { notification } from 'antd';
import * as Yup from 'yup';

import { getNow } from '@libs/NwMoment';

import { useSaveAddressOnContact } from '@hooks/API/Contact/UseContactConnections';
import { getContactName } from '@hooks/API/Contact/UseContactsList';
import {
    useUpdateAccommodation,
    useAddAccommodation,
    useUpdateGroupedCommonAccommodation,
    useUpdateGroupedIndividualAccommodation,
} from '@hooks/API/Event/UseAccommodation';
import { usePreviewProject } from '@hooks/API/Project/UseProject';

import EditEvent from '@components/EventDrawer/EditEvent';
import {
    NWInput,
    NWContactDropdown,
    NWTextAreaCharCounter,
    NWProjectSelector,
    NWLocationSelector,
    NWCheckDatePeriodPicker,
    NWSwitch,
} from '@components/Gui/NwForm/NwFormItems';

const AccommodationForm = ({
    accommodationData,
    editModelEvent,
    viewMode,
    modelId,
    projectId,
    afterUpdate,
    onCancel,
    onUpdateAccommodationData,
    onTouchForm,
}) => {
    // this is only for the Location Selector and can be updated by ProjectSelector
    const { mutateAsync: onAddAccommodation } = useAddAccommodation();
    const { mutateAsync: onUpdateAccommodation } = useUpdateAccommodation();
    const { mutateAsync: onUpdateGroupedIndividualAccommodation } = useUpdateGroupedIndividualAccommodation();
    const { mutateAsync: onUpdateGroupedCommonAccommodation } = useUpdateGroupedCommonAccommodation();
    const [selectedProjectId, setSelectedProjectId] = useState(
        (accommodationData && accommodationData.ProjectID) || projectId
    );
    const { data: project } = usePreviewProject(selectedProjectId);

    // indirizzo da salvare anche sull'anagrafica del service, se l'utente lo chiede
    const [addLocationToCustomer, setAddLocationToCustomer] = useState(null);
    const saveAddressOnContact = useSaveAddressOnContact();

    const submitForm = async (values, setSubmitting) => {
        const addressOnContactError = await saveAddressOnContact({
            addLocationToCustomer,
            address: values.Address,
        });
        if (addressOnContactError) {
            notification.error({ message: 'Error', description: addressOnContactError });
        }

        const newAccommodationData = {
            ...values,
            ...values.Address,
            PhoneNumber: values.Address.Phone,
        };

        delete newAccommodationData.Address;

        if (!accommodationData) {
            try {
                const newAccommodation = await onAddAccommodation({ accommodationData: newAccommodationData });
                if (afterUpdate) {
                    afterUpdate({
                        ...newAccommodation,
                        TypeName: 'Accommodation',
                    });
                }
                onTouchForm(false);
                onUpdateAccommodationData(newAccommodation.ID);
                onCancel();
            } catch (error) {
                console.error(error);
            }
            setSubmitting(false);
        } else {
            newAccommodationData.ID = accommodationData.ID;
            try {
                if (accommodationData.GroupGuid) {
                    if (editModelEvent) {
                        newAccommodationData.ChangeOnAllModels = values.ChangeOnAllModels;
                        await onUpdateGroupedIndividualAccommodation({ accommodationData: newAccommodationData });
                    } else {
                        newAccommodationData.GroupGuid = accommodationData.GroupGuid;
                        delete newAccommodationData.ID;
                        await onUpdateGroupedCommonAccommodation({ accommodationData: newAccommodationData });
                    }
                } else {
                    await onUpdateAccommodation({ accommodationData: newAccommodationData });
                }

                onTouchForm(false);
                if (afterUpdate) {
                    afterUpdate();
                }
                onUpdateAccommodationData();
                if (!viewMode || viewMode === 'create') {
                    onCancel();
                }
            } catch (error) {
                onTouchForm(false);
                console.error('ant : Update Accommodation Error', error);
            }
            setSubmitting(false);
        }
    };

    let initialValues = {
        ProjectID: accommodationData ? accommodationData.ProjectID : projectId ? projectId : '',
        ModelID: accommodationData ? accommodationData.ModelID : modelId ? modelId : '',
        Title: accommodationData ? accommodationData.Title : '',
        ServiceID: accommodationData ? (accommodationData.ServiceID ? accommodationData.ServiceID : null) : null,
        Note: accommodationData ? accommodationData.Note : '',
        FromDate: accommodationData ? accommodationData.FromDate : getNow(true),
        ToDate: accommodationData ? accommodationData.ToDate : getNow(true),
        Address: {
            Country: accommodationData ? accommodationData.Country : '',
            City: accommodationData ? accommodationData.City : '',
            County: accommodationData ? accommodationData.County : '',
            State: accommodationData ? accommodationData.State : '',
            Zip: accommodationData ? accommodationData.Zip : '',
            Street: accommodationData ? accommodationData.Street : '',
            Access: accommodationData ? accommodationData.Access : '',
            Phone: accommodationData ? accommodationData.PhoneNumber : '',
        },
        ReservationCode: accommodationData ? accommodationData.ReservationCode || '' : '',
        CheckDatePeriod: {
            FromDate: accommodationData ? accommodationData.FromDate : getNow(true),
            ToDate: accommodationData ? accommodationData.ToDate : getNow(true),
        },
    };

    const validationSchema = Yup.object().shape({
        Title: Yup.string().required('Content is required'),
        ModelID: Yup.number().required('Content is required'),
    });

    let dataSource = [];

    if (!editModelEvent || !accommodationData) {
        if (!accommodationData) {
            dataSource.push({
                component: NWProjectSelector,
                name: 'ProjectID',
                readOnly: projectId ? true : false,
                Col: 24,
                onAfterChange: (values, _, value) => {
                    if (values.ProjectID !== value) setSelectedProjectId(value);
                },
            });
        }
        dataSource.push({
            component: NWContactDropdown,
            label: 'Service',
            name: 'ServiceID',
            activeTypes: ['srv'],
            activableTypes: [],
            Col: !modelId ? 12 : 24,
            onAfterChange: (values, setFieldValue, value) => {
                if (values.ServiceID !== value && !values.Title) setFieldValue('Title', getContactName(value));
            },
        });
        if (!modelId)
            dataSource.push({
                component: NWContactDropdown,
                label: 'Model',
                name: 'ModelID',
                activeTypes: ['mod'],
                activableTypes: [],
                Col: 12,
            });
        dataSource.push({
            component: NWInput,
            label: 'Title',
            name: 'Title',
            type: 'text',
            Col: 24,
        });
        dataSource.push({
            component: NWCheckDatePeriodPicker,
            name: 'CheckDatePeriod',
            onAfterChange: (_, setFieldValue, value) => {
                if (value) {
                    setFieldValue('FromDate', value.FromDate);
                    setFieldValue('ToDate', value.ToDate);
                }
            },
            Col: 24,
        });
        dataSource.push({
            component: NWLocationSelector,
            label: 'Address',
            name: 'Address',
            value: { ...initialValues.Address },
            eventType: 'accommodation',
            projectCustomerId: project && project.CustomerID,
            customerId: 'ServiceID',
            projectId: selectedProjectId,
            clearOnChange: accommodationData ? false : true,
            addLocationToCustomer: addLocationToCustomer,
            setAddLocationToCustomer: setAddLocationToCustomer,
            Col: 24,
        });
    }

    if (editModelEvent || !accommodationData || accommodationData.RelatedEvents.length === 0) {
        dataSource.push({
            component: NWTextAreaCharCounter,
            label: 'Note',
            name: 'Note',
            type: 'text',
            maxLength: 2000,
            autoSize: { minRows: 4, maxRows: 10 },
            Col: 24,
        });
        dataSource.push({
            component: NWInput,
            label: 'Reservation Code',
            name: 'ReservationCode',
            type: 'text',
            Col: 24,
        });
    }

    if (editModelEvent) {
        dataSource.push({
            component: NWSwitch,
            text: 'apply these changes on all models in this accommodation',
            name: 'ChangeOnAllModels',
            Col: 24,
        });
    }

    return (
        <EditEvent
            submitForm={submitForm}
            mg={0}
            validationSchema={validationSchema}
            dataSource={dataSource}
            initialValues={initialValues}
            onCancel={onCancel}
            validate={() => {}}
            disableReinitialize
            onTouchForm={onTouchForm}
        />
    );
};

export default React.memo(AccommodationForm);
